// Sign-in, "forgot password", first password (invitation), password reset, forced change and the account page.
import { api } from "../lib/api";
import { brand } from "../lib/brand";
import { errorText, t, tl } from "../lib/i18n";
import { Link, navigate } from "../lib/router";
import { homePath, session, setSession, signOut } from "../lib/session";
import { h, local, resource } from "../lib/ui";
import { act, Button, Field, formData, isBusy, LangSwitch, toast, Wordmark } from "./components";
import { Icon, Roofline } from "./icons";

/** A password field with a show/hide button. */
export function PasswordInput(props: { id: string; name: string; autocomplete: string; required?: boolean; placeholder?: string }) {
  const s = local(`pw.${props.id}`, () => ({ show: false }));
  return (
    <div class="pw-wrap">
      <input class="input" id={props.id} name={props.name} type={s.show ? "text" : "password"} autocomplete={props.autocomplete}
        required={props.required} placeholder={props.placeholder} spellcheck={false} autocapitalize="off" />
      <button type="button" class="pw-toggle" aria-label={s.show ? t("auth.hidePassword") : t("auth.showPassword")}
        aria-pressed={String(s.show)} onClick={() => s.set({ show: !s.show })}>
        <Icon name={s.show ? "eyeOff" : "eye"} size={18} />
      </button>
    </div>
  );
}

function AuthFrame(props: { children?: any }) {
  const b = brand.current;
  return (
    <div class="auth">
      <aside class="auth-side">
        <div class="row between">
          <Wordmark />
          <LangSwitch />
        </div>
        <Roofline class="roofline" />
        <div class="stack">
          <h1>{tl(b.tagline) || b.appName}</h1>
          <p>{t("auth.sidePitch")}</p>
        </div>
      </aside>
      <main class="auth-main">
        <div class="auth-card">{props.children}</div>
      </main>
    </div>
  );
}

export function LoginScreen() {
  const s = local("auth.login", () => ({ error: null as string | null }));
  const submit = (e: Event) => {
    const d = formData(e);
    s.set({ error: null });
    act("login", async () => {
      try {
        setSession(await api.post("/api/auth/login", { email: d.email, password: d.password }));
        const next = new URLSearchParams(location.search).get("next");
        navigate(next && next.startsWith("/") && !next.startsWith("//") ? next : homePath(), { replace: true });
      } catch (err) {
        s.set({ error: errorText(err) });
      }
    });
  };
  return (
    <AuthFrame>
      <form class="stack loose" onSubmit={submit} noValidate>
        <div class="stack tight">
          <h2>{t("auth.signInTitle")}</h2>
          <p class="muted">{t("auth.signInBody", { app: brand.current.appName })}</p>
        </div>
        <div class="stack">
          <Field label={t("fields.email")} id="email" hint={t("auth.usernameHint")}>
            <input class="input" id="email" name="email" type="email" autocomplete="username" required />
          </Field>
          <Field label={t("fields.password")} id="password">
            <PasswordInput id="password" name="password" autocomplete="current-password" required />
          </Field>
          {s.error ? <div class="note danger" role="alert">{s.error}</div> : null}
        </div>
        <Button type="submit" size="large" block busy={isBusy("login")}>{t("auth.signIn")}</Button>
        <Link href="/forgot" class="small strong" style="align-self:center">{t("auth.forgotLink")}</Link>
      </form>
    </AuthFrame>
  );
}

export function SetPasswordScreen(props: { token: string; purpose: "invite" | "reset" }) {
  const link = resource(`auth.link.${props.token}`, () => api.get(`/api/auth/link/${encodeURIComponent(props.token)}`));
  const s = local(`auth.set.${props.token}`, () => ({ error: null as string | null }));
  const submit = (e: Event) => {
    const d = formData(e);
    if ((d.password ?? "") !== (d.confirm ?? "")) return s.set({ error: t("auth.mismatch") });
    s.set({ error: null });
    act("setpw", async () => {
      try {
        setSession(await api.post(`/api/auth/link/${encodeURIComponent(props.token)}`, { password: d.password }));
        toast(t("auth.passwordSet"));
        navigate(homePath(), { replace: true });
      } catch (err) {
        s.set({ error: errorText(err) });
      }
    });
  };
  return (
    <AuthFrame>
      {link.error ? (
        <div class="stack">
          <h2>{t("auth.linkInvalidTitle")}</h2>
          <p class="muted">{t("auth.linkInvalidBody")}</p>
          <Button kind="secondary" onClick={() => navigate("/login")}>{t("auth.toSignIn")}</Button>
        </div>
      ) : (
        <form class="stack loose" onSubmit={submit} noValidate>
          <div class="stack tight">
            <h2>{props.purpose === "invite" ? t("auth.welcomeTitle", { name: link.data?.name ?? "" }) : t("auth.resetTitle")}</h2>
            <p class="muted">{props.purpose === "invite" ? t("auth.welcomeBody", { app: brand.current.appName }) : t("auth.resetBody")}</p>
          </div>
          <div class="stack">
            <Field label={t("fields.email")}>
              <input class="input" value={link.data?.email ?? ""} readOnly autocomplete="username" />
            </Field>
            <Field label={t("auth.newPassword")} id="pw" hint={t("auth.passwordRule")}>
              <PasswordInput id="pw" name="password" autocomplete="new-password" required />
            </Field>
            <Field label={t("auth.confirmPassword")} id="pw2">
              <PasswordInput id="pw2" name="confirm" autocomplete="new-password" required />
            </Field>
            {s.error ? <div class="note danger" role="alert">{s.error}</div> : null}
          </div>
          <Button type="submit" size="large" block busy={isBusy("setpw")}>{t("auth.savePassword")}</Button>
        </form>
      )}
    </AuthFrame>
  );
}

/** "Forgot your password?": emails a reset link when email is set up, otherwise says who can help. */
export function ForgotScreen() {
  const s = local("auth.forgot", () => ({ sent: null as string | null, error: null as string | null }));
  const contact = brand.current.supportEmail || t("auth.yourContact");
  const submit = (e: Event) => {
    const d = formData(e);
    s.set({ error: null });
    act("forgot", async () => {
      try {
        const res = await api.post("/api/auth/forgot", { email: d.email });
        if (res.email === false) brand.current.emailEnabled = false;
        s.set({ sent: res.email === false ? null : d.email });
      } catch (err) {
        s.set({ error: errorText(err) });
      }
    });
  };
  return (
    <AuthFrame>
      <div class="stack loose">
        <div class="stack tight">
          <h2>{s.sent ? t("auth.forgotSentTitle") : t("auth.forgotTitle")}</h2>
          {s.sent ? <p>{t("auth.forgotSent", { email: s.sent })}</p>
            : brand.current.emailEnabled ? <p class="muted">{t("auth.forgotBody")}</p>
            : <div class="note">{t("auth.forgotNoEmail", { contact })}</div>}
        </div>
        {!s.sent && brand.current.emailEnabled ? (
          <form class="stack loose" onSubmit={submit} noValidate>
            <Field label={t("fields.email")} id="f-email">
              <input class="input" id="f-email" name="email" type="email" autocomplete="username" required />
            </Field>
            {s.error ? <div class="note danger" role="alert">{s.error}</div> : null}
            <Button type="submit" size="large" block busy={isBusy("forgot")}>{t("auth.sendLink")}</Button>
          </form>
        ) : null}
        <Link href="/login" class="small strong" style="align-self:center">{t("auth.backToSignIn")}</Link>
      </div>
    </AuthFrame>
  );
}

/** Shown right after signing in with a temporary password: nothing else is reachable until a new one is chosen. */
export function MustChangeScreen() {
  const me = session.me!;
  const s = local("auth.mustchange", () => ({ error: null as string | null }));
  const submit = (e: Event) => {
    const d = formData(e);
    if ((d.next ?? "") !== (d.confirm ?? "")) return s.set({ error: t("auth.mismatch") });
    s.set({ error: null });
    act("mustchange", async () => {
      try {
        await api.post("/api/me/password", { next: d.next ?? "" });
        setSession(await api.get("/api/me"));
        toast(t("auth.passwordSet"));
        navigate(homePath(), { replace: true });
      } catch (err) {
        s.set({ error: errorText(err) });
      }
    });
  };
  return (
    <AuthFrame>
      <form class="stack loose" onSubmit={submit} noValidate>
        <div class="stack tight">
          <h2>{t("auth.mustChangeTitle")}</h2>
          <p class="muted">{t("auth.mustChangeBody")}</p>
        </div>
        <div class="stack">
          <Field label={t("fields.email")}><input class="input" value={me.email} readOnly autocomplete="username" /></Field>
          <Field label={t("auth.newPassword")} id="mc1" hint={t("auth.passwordRule")}><PasswordInput id="mc1" name="next" autocomplete="new-password" required /></Field>
          <Field label={t("auth.confirmPassword")} id="mc2"><PasswordInput id="mc2" name="confirm" autocomplete="new-password" required /></Field>
          {s.error ? <div class="note danger" role="alert">{s.error}</div> : null}
        </div>
        <Button type="submit" size="large" block busy={isBusy("mustchange")}>{t("auth.mustChangeSave")}</Button>
        <button type="button" class="link-btn small" style="align-self:center" onClick={async () => { await signOut(); navigate("/login"); }}>{t("common.signOut")}</button>
      </form>
    </AuthFrame>
  );
}

/** Account page content (name, phone, language, password) — rendered inside each app's frame. */
export function AccountPanel() {
  const me = session.me!;
  const s = local("account", () => ({ pwError: null as string | null }));
  const saveProfile = (e: Event) => {
    const d = formData(e);
    act("acct.profile", async () => {
      await api.patch("/api/me", { name: d.name, phone: d.phone });
      me.name = d.name;
      me.phone = d.phone;
    }, t("common.saved"));
  };
  const savePw = (e: Event) => {
    const d = formData(e);
    const form = e.currentTarget as HTMLFormElement;
    if (d.next !== d.confirm) return s.set({ pwError: t("auth.mismatch") });
    s.set({ pwError: null });
    act("acct.pw", async () => {
      try {
        await api.post("/api/me/password", { current: d.current ?? "", next: d.next ?? "" });
        form.reset();
        toast(t("account.passwordChanged"));
      } catch (err) {
        s.set({ pwError: errorText(err) });
      }
    });
  };
  return (
    <div class="stack loose">
      <section class="panel">
        <div class="panel-head"><h2>{t("account.profile")}</h2></div>
        <form class="panel-body stack" onSubmit={saveProfile}>
          <div class="cols-2">
            <Field label={t("fields.name")} id="acct-name"><input class="input" id="acct-name" name="name" value={me.name} required /></Field>
            <Field label={t("fields.phone")} id="acct-phone"><input class="input" id="acct-phone" name="phone" type="tel" value={me.phone ?? ""} /></Field>
          </div>
          <Field label={t("fields.email")}><input class="input" value={me.email} readOnly /></Field>
          <div class="row between">
            <div class="row"><span class="label">{t("common.language")}</span><LangSwitch /></div>
            <Button type="submit" busy={isBusy("acct.profile")}>{t("common.save")}</Button>
          </div>
        </form>
      </section>
      <section class="panel">
        <div class="panel-head"><h2>{t("account.password")}</h2></div>
        <form class="panel-body stack" onSubmit={savePw}>
          <Field label={t("account.currentPassword")} id="cur"><PasswordInput id="cur" name="current" autocomplete="current-password" required /></Field>
          <div class="cols-2">
            <Field label={t("auth.newPassword")} id="np" hint={t("auth.passwordRule")}><PasswordInput id="np" name="next" autocomplete="new-password" required /></Field>
            <Field label={t("auth.confirmPassword")} id="np2"><PasswordInput id="np2" name="confirm" autocomplete="new-password" required /></Field>
          </div>
          {s.pwError ? <div class="note danger" role="alert">{s.pwError}</div> : null}
          <div class="row end"><Button type="submit" busy={isBusy("acct.pw")}>{t("account.changePassword")}</Button></div>
        </form>
      </section>
      <div class="row end">
        <Button kind="danger-outline" icon="logout" onClick={async () => { await signOut(); navigate("/login"); }}>{t("common.signOut")}</Button>
      </div>
    </div>
  );
}

